import { EventData } from "lib/tracking";
import { LeadEntityType } from "lib/types/graphQLEnums";

/** The organisation side a caller added, which the mutation's `organisation` type can't tell. */
export type OrganisationDataType = "BUYER" | "SUPPLIER";

const DATA_TYPE_BY_ENTITY_TYPE: Record<LeadEntityType, string> = {
  [LeadEntityType.Notice]: "NOTICE",
  [LeadEntityType.Document]: "DOCUMENT",
  [LeadEntityType.Framework]: "FRAMEWORK",
  [LeadEntityType.Contact]: "CONTACT",
  [LeadEntityType.ContactCognism]: "CONTACT",
  [LeadEntityType.BuyerActivityReport]: "BUYER_ACTIVITY_REPORT",
  // A free-text buyer; `Free-text buyer` on the event tells it apart from a Stotles buyer.
  [LeadEntityType.CustomOrganisation]: "BUYER",
  [LeadEntityType.Buyer]: "BUYER",
  [LeadEntityType.Supplier]: "SUPPLIER",
  // An organisation named without a role, so the caller's side decides (see below).
  [LeadEntityType.Organisation]: "ORGANISATION",
};

function isOrganisationDataType(value: unknown): value is OrganisationDataType {
  return value === "BUYER" || value === "SUPPLIER";
}

/**
 * The `Data type` (and, for contacts, `Contact source`) of a lead entity add or remove, derived
 * from the entities themselves so no caller can leave it out. Several types join with ", ".
 * An `organisation` reports the caller's own `Data type` when that says BUYER or SUPPLIER.
 */
export function leadEntityEventData(
  entityTypes: LeadEntityType[],
  trackingData?: EventData,
): EventData {
  const callerDataType = trackingData?.["Data type"];
  const dataTypes = new Set(
    entityTypes.map((type) =>
      type === LeadEntityType.Organisation && isOrganisationDataType(callerDataType)
        ? callerDataType
        : DATA_TYPE_BY_ENTITY_TYPE[type],
    ),
  );
  const data: EventData = { "Data type": [...dataTypes].sort().join(", ") };

  const contactSources = new Set<string>();
  if (entityTypes.includes(LeadEntityType.Contact)) contactSources.add("Oscar");
  if (entityTypes.includes(LeadEntityType.ContactCognism)) contactSources.add("Cognism");
  if (contactSources.size > 0) data["Contact source"] = [...contactSources].sort().join(", ");

  return data;
}
